import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Vordefinierte Zeitoptionen in Minuten
const TIME_PRESETS = [
  { value: 15, label: "15 Minuten" },
  { value: 30, label: "30 Minuten" },
  { value: 60, label: "1 Stunde" },
  { value: 120, label: "2 Stunden" },
  { value: 240, label: "4 Stunden" },
  { value: 480, label: "8 Stunden" },
];

// Vordefinierte Optionen für Login-Versuche
const ATTEMPT_PRESETS = [
  { value: 3, label: "3" },
  { value: 5, label: "5" },
  { value: 10, label: "10" },
];

// Vordefinierte Optionen für Sperrzeit
const LOCKOUT_PRESETS = [
  { value: 5, label: "5 Min." },
  { value: 15, label: "15 Min." },
  { value: 30, label: "30 Min." },
  { value: 60, label: "1 Std." },
];

export function SessionSettingsManager({ authToken }) {
  const [settings, setSettings] = useState({
    auto_logout_enabled: true,
    auto_logout_minutes: 30,
    login_max_attempts: 5,
    login_lockout_minutes: 15,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  // Einstellungen laden
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/session-settings`, authHeaders());
        setSettings(res.data);
      } catch (error) {
        console.error("Fehler beim Laden der Session-Einstellungen:", error);
      }
      setLoading(false);
    };
    loadSettings();
  }, [authHeaders]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/api/session-settings`, settings, authHeaders());
      setSettings(res.data);
      setMessage({ type: "success", text: "Session-Einstellungen gespeichert!" });
      setHasChanges(false);
      // Event auslösen, damit die App die neuen Einstellungen anwendet
      window.dispatchEvent(new CustomEvent('session-settings-updated', { detail: res.data }));
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      const errMsg = error?.response?.data?.detail || "Fehler beim Speichern";
      setMessage({ type: "error", text: errMsg });
    }
    setSaving(false);
  };

  // Zeit formatieren
  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} Minuten`;
    } else if (minutes === 60) {
      return "1 Stunde";
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours} Std. ${mins} Min.` : `${hours} Stunden`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="session-settings-manager">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-amber-50 to-white p-4 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-900" style={{ fontSize: 'var(--font-h3-size)', fontWeight: 'var(--font-h3-weight)' }}>
              Session-Einstellungen
            </h3>
            <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>
              Konfigurieren Sie das automatische Logout bei Inaktivität.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="rounded-lg bg-amber-600 px-4 py-2 text-white hover:bg-amber-500 disabled:opacity-50"
            style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            {saving ? "Speichern..." : "Speichern"}
          </button>
        </div>

        {/* Status-Meldung */}
        {message && (
          <div
            className={`mt-3 rounded-lg p-3 ${
              message.type === "success"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700"
            }`}
            style={{ fontSize: 'var(--font-body-size)' }}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Auto-Logout aktivieren */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
              Automatisches Logout
            </h4>
            <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>
              Benutzer werden nach einer bestimmten Zeit der Inaktivität automatisch abgemeldet
            </p>
          </div>
          <button
            onClick={() => handleChange("auto_logout_enabled", !settings.auto_logout_enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.auto_logout_enabled ? "bg-amber-600" : "bg-slate-200"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                settings.auto_logout_enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Zeit-Einstellung */}
      {settings.auto_logout_enabled && (
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h4 className="mb-3 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
            Inaktivitätszeit bis zum Logout
          </h4>
          
          {/* Schnellauswahl */}
          <div className="flex flex-wrap gap-2 mb-4">
            {TIME_PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => handleChange("auto_logout_minutes", preset.value)}
                className={`rounded-full px-4 py-2 transition-all ${
                  settings.auto_logout_minutes === preset.value
                    ? "bg-amber-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Benutzerdefinierte Eingabe */}
          <div className="flex items-center gap-3">
            <label className="text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
              Oder manuell eingeben:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.auto_logout_minutes}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= 480) {
                    handleChange("auto_logout_minutes", val);
                  }
                }}
                min="1"
                max="480"
                className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-center focus:border-amber-500 focus:outline-none"
                style={{ fontSize: 'var(--font-body-size)' }}
              />
              <span className="text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>Minuten</span>
            </div>
          </div>

          {/* Anzeige der aktuellen Einstellung */}
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <p className="text-amber-800" style={{ fontSize: 'var(--font-body-size)' }}>
              <strong>Aktuelle Einstellung:</strong> Benutzer werden nach{" "}
              <span className="font-semibold">{formatTime(settings.auto_logout_minutes)}</span>{" "}
              Inaktivität automatisch abgemeldet.
            </p>
          </div>
        </div>
      )}

      {/* Deaktiviert-Hinweis */}
      {!settings.auto_logout_enabled && (
        <div className="rounded-lg bg-slate-100 p-3 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
          <strong>Hinweis:</strong> Bei deaktiviertem Auto-Logout bleiben Benutzer dauerhaft angemeldet. 
          Dies kann ein Sicherheitsrisiko darstellen, besonders auf geteilten Geräten.
        </div>
      )}

      {/* Info-Box */}
      <div className="rounded-lg bg-blue-50 p-3 text-blue-700" style={{ fontSize: 'var(--font-body-size)' }}>
        <strong>Wie funktioniert es?</strong> Die Inaktivitätszeit wird bei jeder Benutzerinteraktion 
        (Mausbewegung, Tastatureingabe, Klick) zurückgesetzt. Nach Ablauf der Zeit wird der Benutzer 
        automatisch zur Anmeldeseite weitergeleitet.
      </div>

      {/* ==================== LOGIN-SCHUTZ (Brute Force) ==================== */}
      <div className="rounded-xl bg-gradient-to-r from-rose-50 to-white p-4 shadow-sm mt-6">
        <div>
          <h3 className="font-bold text-slate-900" style={{ fontSize: 'var(--font-h3-size)', fontWeight: 'var(--font-h3-weight)' }}>
            Login-Schutz (Brute-Force)
          </h3>
          <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>
            Schutz gegen wiederholte fehlerhafte Anmeldeversuche. Nach Erreichen der maximalen Versuche wird das Konto temporär gesperrt.
          </p>
        </div>
      </div>

      {/* Max. Login-Versuche */}
      <div className="rounded-xl bg-white p-4 shadow-sm" data-testid="login-max-attempts-section">
        <h4 className="mb-3 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Max. fehlgeschlagene Anmeldeversuche
        </h4>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {ATTEMPT_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => handleChange("login_max_attempts", preset.value)}
              className={`rounded-full px-4 py-2 transition-all ${
                settings.login_max_attempts === preset.value
                  ? "bg-rose-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
              data-testid={`attempt-preset-${preset.value}`}
            >
              {preset.label} Versuche
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <label className="text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            Oder manuell eingeben:
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={settings.login_max_attempts}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 50) {
                  handleChange("login_max_attempts", val);
                }
              }}
              min="1"
              max="50"
              className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-center focus:border-rose-500 focus:outline-none"
              style={{ fontSize: 'var(--font-body-size)' }}
              data-testid="login-max-attempts-input"
            />
            <span className="text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>Versuche</span>
          </div>
        </div>
      </div>

      {/* Sperrzeit */}
      <div className="rounded-xl bg-white p-4 shadow-sm" data-testid="login-lockout-minutes-section">
        <h4 className="mb-3 text-slate-900" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Sperrzeit nach Überschreitung
        </h4>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {LOCKOUT_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => handleChange("login_lockout_minutes", preset.value)}
              className={`rounded-full px-4 py-2 transition-all ${
                settings.login_lockout_minutes === preset.value
                  ? "bg-rose-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
              data-testid={`lockout-preset-${preset.value}`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <label className="text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
            Oder manuell eingeben:
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={settings.login_lockout_minutes}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 1440) {
                  handleChange("login_lockout_minutes", val);
                }
              }}
              min="1"
              max="1440"
              className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-center focus:border-rose-500 focus:outline-none"
              style={{ fontSize: 'var(--font-body-size)' }}
              data-testid="login-lockout-minutes-input"
            />
            <span className="text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>Minuten</span>
          </div>
        </div>
      </div>

      {/* Aktuelle Einstellung Login-Schutz */}
      <div className="rounded-lg bg-rose-50 p-3" data-testid="login-protection-summary">
        <p className="text-rose-800" style={{ fontSize: 'var(--font-body-size)' }}>
          <strong>Aktuelle Einstellung:</strong> Nach{" "}
          <span className="font-semibold">{settings.login_max_attempts}</span>{" "}
          fehlgeschlagenen Anmeldeversuchen wird das Konto für{" "}
          <span className="font-semibold">{settings.login_lockout_minutes} Minute(n)</span>{" "}
          gesperrt.
        </p>
      </div>

      {/* Info-Box Login-Schutz */}
      <div className="rounded-lg bg-blue-50 p-3 text-blue-700" style={{ fontSize: 'var(--font-body-size)' }}>
        <strong>Wie funktioniert der Login-Schutz?</strong> Bei jedem fehlgeschlagenen Anmeldeversuch wird ein Zähler erhöht. 
        Wird das Maximum erreicht, wird die E-Mail-Adresse für die konfigurierte Sperrzeit blockiert. 
        Nach erfolgreichem Login wird der Zähler zurückgesetzt.
      </div>
    </div>
  );
}
